import React from 'react';
import { useNavigate } from 'react-router-dom';

import useKeyDownEvent from '../../hooks/useKeyDownEvent';
import { useUserStatusContext } from '../../context/UserStatus';
import loadingAnimation from '../States/Loading/gifs/hero_animation.gif';
import { SearchDestination, SearchSuggestion, UseSearchSuggestions } from './types';

import * as Style from './style.module.scss';

// Minimum characters before we hit the API.
export const MIN_SEARCH_LENGTH = 3;

type Props = {
    // Already-debounced, length-gated query (empty string = nothing to search).
    effectiveQuery: string;
    useSearchSuggestions: UseSearchSuggestions;
    // Whether the shared popover is open (gates keyboard navigation).
    isOpen: boolean;
    wrapperRef: React.RefObject<HTMLDivElement>;
    // Closes the shared popover (e.g. after selecting a suggestion).
    onClose: () => void;
    // Runs the search and updates the main list (same as pressing Enter).
    onShowAll: () => void;
    // Hands the top suggestion's own list up to SearchForm, which applies it to whichever submit
    // path fires next — the button here, or Enter with nothing highlighted.
    onTopSuggestionChange: (showAllTo?: SearchDestination) => void;
};

// Renders the quick-result suggestions *content* inside the shared search
// popover. The popover box and its open/close state are owned by SearchBar.
const SearchSuggestions: React.FC<Props> = ({
    effectiveQuery,
    useSearchSuggestions,
    isOpen,
    wrapperRef,
    onClose,
    onShowAll,
    onTopSuggestionChange
}) => {
    const navigate = useNavigate();
    const { activeOrgId, setActiveOrg } = useUserStatusContext();
    // Index of the keyboard-highlighted suggestion; -1 means none (the input).
    const [activeIndex, setActiveIndex] = React.useState(-1);
    // Mirror into a ref so the keydown handler can read it without being
    // recreated (and the document listener re-subscribed) on every keystroke.
    const activeIndexRef = React.useRef(activeIndex);
    React.useEffect(() => {
        activeIndexRef.current = activeIndex;
    }, [activeIndex]);

    const { suggestions: results, isFetching } = useSearchSuggestions(effectiveQuery);

    // Reset the keyboard highlight whenever the search term changes.
    React.useEffect(() => {
        setActiveIndex(-1);
    }, [effectiveQuery]);

    // A full search should land on the list holding the best match, so the first row decides the
    // destination. Reported upward rather than passed through `onShowAll` because Enter submits
    // via the form and never reaches this component.
    React.useEffect(() => {
        onTopSuggestionChange(results[0]?.showAllTo);
    }, [results, onTopSuggestionChange]);

    const handleSelect = React.useCallback(
        (suggestion: SearchSuggestion) => {
            // Cross-provider: if the result lives in another tenant, switch the active org first so
            // the destination page loads under the right provider. Single-org / leads have no heroOrgId.
            if (suggestion.heroOrgId && suggestion.heroOrgId !== activeOrgId) {
                setActiveOrg(suggestion.heroOrgId);
            }
            onClose();
            navigate(suggestion.to);
        },
        [navigate, onClose, activeOrgId, setActiveOrg]
    );

    const handleKeydown = React.useCallback(
        (e: KeyboardEvent) => {
            // Only steer suggestions while the popover is open and focus is in the search.
            if (!isOpen || !wrapperRef.current?.contains(document.activeElement)) {
                return;
            }

            if (e.key === 'ArrowDown' && results.length) {
                e.preventDefault();
                setActiveIndex((i) => Math.min(i + 1, results.length - 1));
            } else if (e.key === 'ArrowUp' && results.length) {
                e.preventDefault();
                setActiveIndex((i) => Math.max(i - 1, -1));
            } else if (e.key === 'Enter') {
                const active = results[activeIndexRef.current];
                if (active) {
                    // Enter on a highlighted suggestion acts as a click.
                    e.preventDefault();
                    handleSelect(active);
                } else {
                    // Enter with nothing highlighted searches (form submit) and
                    // closes the popover.
                    onClose();
                }
            }
        },
        [isOpen, results, handleSelect, onClose, wrapperRef]
    );

    useKeyDownEvent(handleKeydown);

    const content = !effectiveQuery ? (
        <div className={Style.message}>Type at least {MIN_SEARCH_LENGTH} characters to search</div>
    ) : isFetching ? (
        <div className={Style.loading}>
            <img src={loadingAnimation} alt="Loading" className={Style.loadingImage} />
        </div>
    ) : results.length === 0 ? (
        <div className={Style.message}>No results found</div>
    ) : (
        results.map((suggestion, index) => (
            <button
                key={suggestion.id}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                className={[Style.option, index === activeIndex && Style.active]
                    .filter(Boolean)
                    .join(' ')}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => handleSelect(suggestion)}
            >
                <span className={Style.optionText}>
                    <span className={Style.title}>{suggestion.title}</span>
                    {suggestion.subtitle && (
                        <span className={Style.subtitle}>{suggestion.subtitle}</span>
                    )}
                    {suggestion.footnote && (
                        <span className={Style.footnote}>{suggestion.footnote}</span>
                    )}
                </span>
                {suggestion.tag && <span className={Style.tag}>{suggestion.tag}</span>}
                {suggestion.aside && <span className={Style.aside}>{suggestion.aside}</span>}
            </button>
        ))
    );

    return (
        <>
            <div className={Style.suggestionsList}>{content}</div>
            {results.length > 0 && (
                <button type="button" className={Style.showAll} onClick={onShowAll}>
                    Show all results
                </button>
            )}
        </>
    );
};

export default SearchSuggestions;
